import { useEffect, useState } from "react";
import { API_BASE_URL, ENDPOINTS } from "../config/api";
import { authFetch, safeJson } from "../utils/auth";
import airtelMoneyLogo from "../assets/images/payments/airtel_money.png";
import mpambaLogo from "../assets/images/payments/mpamba.png";

// One payment channel as the backend describes it
export interface PaymentChannel {
  name: string;
  slug: string;
  is_active: boolean;
  icon?: string;
}

// Local logos we already have, keyed by channel slug
const LOGOS: Record<string, string> = {
  airtel_money: airtelMoneyLogo,
  mpamba: mpambaLogo,
  paychangu_airtel_money: airtelMoneyLogo,
  paychangu_mpamba: mpambaLogo,
};

// Logo to show: our local one if we have it, otherwise the backend's icon
export function logoFor(ch: PaymentChannel): string {
  return LOGOS[ch.slug] || ch.icon || "";
}

// Mobile money and USSD channels need a phone number
export function needsPhone(ch: PaymentChannel): boolean {
  return /mpamba|airtel|ussd/i.test(ch.slug);
}
// Card and bank channels need an email for the receipt
export function needsEmail(ch: PaymentChannel): boolean {
  return /card|bank/i.test(ch.slug);
}

// Operator name the backend expects for mobile money
export function operatorFor(ch: PaymentChannel): "tnm" | "airtel" | undefined {
  if (/mpamba/i.test(ch.slug)) return "tnm";
  if (/airtel/i.test(ch.slug)) return "airtel";
  return undefined;
}

// Phone number prefix the input starts with
export function prefixFor(ch: PaymentChannel): string {
  if (/mpamba/i.test(ch.slug)) return "2658";
  if (/airtel/i.test(ch.slug)) return "2659";
  return "265";
}

// Loads the channels and keeps only the active ones
export function usePaymentChannels() {
  const [channels, setChannels] = useState<PaymentChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false; // stops state updates if the page is left mid-request

    async function load() {
      try {
        const res = await authFetch(
          `${API_BASE_URL}${ENDPOINTS.PAYMENT_CHANNELS}`,
          {},
          15000,
        );
        const json = await safeJson(res);
        if (!res.ok || !json) throw new Error("Bad response");

        // The list may be an array or an object keyed by name, so handle both
        const raw = json.data ?? json;
        const list = (
          Array.isArray(raw) ? raw : Object.values(raw || {})
        ) as PaymentChannel[];

        if (!cancelled)
          setChannels(list.filter((c) => c?.is_active && c?.slug));
      } catch {
        if (!cancelled)
          setError("Could not load payment methods. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { channels, loading, error };
}
